import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Cart-drawer cross-sell rail. Each "+ Add" button adds a variant to the cart
 * using Horizon's native flow (POST cart_add_url with the cart section ids, then
 * dispatch CartAddEvent) so the drawer morphs/refreshes — re-computing the
 * free-shipping bar and dropping the just-added product from the rail.
 */
class CartUpsellComponent extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = async (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const button = target.closest('[data-variant-id]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    event.preventDefault();

    const id = button.dataset.variantId;
    if (!id) return;

    const original = button.innerHTML;
    button.disabled = true;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean);

    const body = new FormData();
    body.set('id', id);
    body.set('quantity', '1');
    if (sectionIds.length) body.set('sections', [...new Set(sectionIds)].join(','));

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('javascript', { body }));
      const data = await response.json();

      if (data.status) {
        button.innerHTML = original;
        button.disabled = false;
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
        return;
      }

      let cart = undefined;
      try {
        cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
      } catch (_) {
        /* totals come from the morphed section regardless */
      }

      document.dispatchEvent(
        new CartAddEvent(cart, this.id || 'cart-upsell', {
          source: 'cart-upsell',
          // The header cart-icon reads `itemCount ?? 0` and hides the badge
          // when it's missing, so pass the new absolute cart count.
          itemCount: cart?.item_count,
          sections: data.sections,
          productId: button.dataset.productId,
          variantId: id,
        })
      );
    } catch (_) {
      button.innerHTML = original;
      button.disabled = false;
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
